import {
  CampaignTriggerTypeEnum,
  ChannelEnum,
  Errors,
  LaunchCampaignSchema,
  type CampaignTriggerType,
  type Channel,
} from "schema"

const CONFIGURATION_TAB_VALUES = [
  "brief",
  "setup",
  "channel",
  "trigger",
  "openingMessages",
  "templates",
  "goals",
] as const

export type ConfigurationTabValue = (typeof CONFIGURATION_TAB_VALUES)[number]

export function isConfigurationTabValue(value: string): value is ConfigurationTabValue {
  return (CONFIGURATION_TAB_VALUES as readonly string[]).includes(value)
}

const TRIGGER_TABS_WITH_CONFIG = [
  CampaignTriggerTypeEnum.QR,
  CampaignTriggerTypeEnum.InstagramDirectMessage,
  CampaignTriggerTypeEnum.InstagramCommentReply,
  CampaignTriggerTypeEnum.MessengerDirectMessage,
  CampaignTriggerTypeEnum.MessengerCommentReply,
  CampaignTriggerTypeEnum.Link,
  CampaignTriggerTypeEnum.Widget,
  CampaignTriggerTypeEnum.Webhook,
  CampaignTriggerTypeEnum.Shopify,
  CampaignTriggerTypeEnum.BigBlue,
] as const

export function shouldShowChannelConfigTab(channel: Channel | null | undefined) {
  return (
    channel === ChannelEnum.Api || channel === ChannelEnum.Gmail || channel === ChannelEnum.Outlook
  )
}

export function shouldShowTriggerConfigTab(trigger: CampaignTriggerType | null | undefined) {
  if (!trigger) return false
  return TRIGGER_TABS_WITH_CONFIG.includes(trigger as (typeof TRIGGER_TABS_WITH_CONFIG)[number])
}

export function isWhatsappVisualTrigger(
  channel: Channel | null | undefined,
  trigger: CampaignTriggerType | null | undefined,
) {
  return (
    channel === ChannelEnum.Whatsapp &&
    (trigger === CampaignTriggerTypeEnum.QR ||
      trigger === CampaignTriggerTypeEnum.Widget ||
      trigger === CampaignTriggerTypeEnum.Link)
  )
}

export function isWhatsappTemplateTrigger(
  channel: Channel | null | undefined,
  trigger: CampaignTriggerType | null | undefined,
) {
  return (
    channel === ChannelEnum.Whatsapp &&
    trigger != null &&
    trigger !== CampaignTriggerTypeEnum.WhatsappDefault &&
    trigger !== CampaignTriggerTypeEnum.QR &&
    trigger !== CampaignTriggerTypeEnum.Widget &&
    trigger !== CampaignTriggerTypeEnum.Link
  )
}

// Maps a launch-validation error to the tab where the user fills the offending
// field. Refines without a path are matched by their Errors message; plain
// field errors fall back to the first path segment.
const launchErrorMessageToTab: Partial<Record<string, ConfigurationTabValue>> = {
  [Errors.CAMPAIGN_FLOW_REQUIRED]: "setup",
  [Errors.CAMPAIGN_CHANNEL_REQUIRED]: "setup",
  [Errors.CAMPAIGN_TRIGGER_INVALID]: "setup",
  [Errors.CAMPAIGN_CALLBACK_URL_REQUIRED]: "channel",
  [Errors.CAMPAIGN_CHANNEL_GMAIL_WITHOUT_ACCOUNT]: "channel",
  [Errors.CAMPAIGN_CHANNEL_OUTLOOK_WITHOUT_ACCOUNT]: "channel",
  [Errors.CAMPAIGN_NUMBER_OR_INTEGRATION_ID_REQUIRED]: "templates",
  [Errors.CAMPAIGN_TEMPLATES_REQUIRED]: "templates",
  [Errors.CAMPAIGN_DEFAULT_LANGUAGE_REQUIRED]: "templates",
  [Errors.CAMPAIGN_DEFAULT_LANGUAGE_TEMPLATE_REQUIRED]: "templates",
  [Errors.CAMPAIGN_OPENING_MESSAGES_REQUIRED]: "openingMessages",
  [Errors.CAMPAIGN_TRIGGER_QR_WITHOUT_QR]: "trigger",
  [Errors.CAMPAIGN_TRIGGER_INSTAGRAM_WITHOUT_ACCOUNT]: "trigger",
  [Errors.CAMPAIGN_TRIGGER_INSTAGRAM_INVALID_AUDIENCE]: "trigger",
}

const launchErrorFieldToTab: Partial<Record<string, ConfigurationTabValue>> = {
  flow: "setup",
  channel: "setup",
  trigger: "setup",
  callbackUrl: "channel",
  callbackHeaders: "channel",
  whatsappNumberId: "templates",
  defaultLanguage: "templates",
  templates: "templates",
  openingMessages: "openingMessages",
  goals: "goals",
}

type LaunchIssue = { message: string; path: PropertyKey[] }

function resolveTabForLaunchIssue(issue: LaunchIssue) {
  const [firstPath, secondPath] = issue.path
  if (firstPath === "triggerIntegration") {
    if (secondPath === "openingEmail") return "channel"
    if (
      issue.message === Errors.CAMPAIGN_CHANNEL_GMAIL_WITHOUT_ACCOUNT ||
      issue.message === Errors.CAMPAIGN_CHANNEL_OUTLOOK_WITHOUT_ACCOUNT
    ) {
      return "channel"
    }
    return "trigger"
  }

  const byMessage = launchErrorMessageToTab[issue.message]
  if (byMessage) return byMessage

  if (typeof firstPath === "string") return launchErrorFieldToTab[firstPath] ?? null

  return null
}

// Returns how many required fields are still pending per configuration tab,
// derived from the same schema the launch action validates against.
export function getMissingFieldCountByTab(formValues: unknown) {
  const counts = new Map<ConfigurationTabValue, number>()

  const parsed = LaunchCampaignSchema.safeParse(formValues)
  if (parsed.success) return counts

  for (const issue of parsed.error.issues) {
    const tab = resolveTabForLaunchIssue(issue)
    if (tab) counts.set(tab, (counts.get(tab) ?? 0) + 1)
  }

  return counts
}
